'use client';
import { useRouter } from 'next/navigation';
import Image from 'next/image';
import Button from '../components/ui/Button';

export default function WhyChooseUsSection() {
  const router = useRouter();

  const handleCTA = () => {
    router?.push('/register');
  };

  return (
    <section className="w-full bg-[#e5d7e9] py-10 sm:py-14 lg:py-20">
      <div className="w-full max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-36 relative">
        
        {/* Background Decorative Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-16 right-16 w-32 sm:w-48 lg:w-64 h-32 sm:h-48 lg:h-64 bg-[#8923b30c] rounded-full"></div>
          <div className="absolute bottom-32 left-8 w-48 sm:w-72 lg:w-96 h-48 sm:h-72 lg:h-96 bg-[#7b287d0c] rounded-full"></div>
        </div>

        {/* Header */}
        <div className="flex flex-col gap-1 justify-start items-center w-full mb-10 sm:mb-12 lg:mb-16 relative z-10">
          <Button
            text="WHY CHOOSE US"
            text_font_size="text-xs"
            text_font_family="Helvetica"
            text_font_weight="font-normal"
            text_line_height="leading-xs"
            text_text_align="center"
            text_text_transform="uppercase"
            text_color="text-white"
            fill_background_color="bg-[#8923b3]"
            border_border_radius="rounded-[20px]"
            padding="12px 14px"
            className="mb-1"
          />
          
          <div className="flex justify-center items-center w-full px-2 sm:px-4 lg:px-14">
            <h2 className="text-[40px] sm:text-[60px] lg:text-[90px] font-bebas font-normal leading-[1.1] text-center text-[#3f1651]">
              Why Purple Cow
            </h2>
          </div>
        </div>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8 mb-10 sm:mb-12 lg:mb-16 relative z-10">
          
          {/* Global Team */}
          <div className="flex flex-col gap-4 sm:gap-5 justify-start items-start w-full h-auto border-2 border-[#8923b3] rounded-[24px] bg-white p-5 sm:p-6 lg:p-9">
            <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-[#8923b3] rounded-[16px] p-3 sm:p-3.5 lg:p-4">
              <Image src="/images/img_icon_56x56.svg" alt="Global team icon" width={64} height={64} className="w-full h-full" />
            </div>
            
            <h3 className="text-[18px] sm:text-[22px] lg:text-[28px] font-bebas font-normal leading-[1.2] text-left text-[#3f1651]">
              A Global Team That Works While You Sleep
            </h3>
            
            <p className="text-[13px] sm:text-[15px] lg:text-base font-poppins font-normal leading-[1.6] text-left text-[#666666]">
              California plus the Philippines gives you speed and efficiency.
            </p>
          </div>

          {/* Clear Pricing */}
          <div className="flex flex-col gap-4 sm:gap-5 justify-start items-start w-full h-auto border-2 border-[#8923b3] rounded-[24px] bg-white p-5 sm:p-6 lg:p-9">
            <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-[#8923b3] rounded-[16px] p-3 sm:p-3.5 lg:p-4">
              <Image src="/images/img_icon_5.svg" alt="Pricing icon" width={64} height={64} className="w-full h-full" />
            </div>
            
            <h3 className="text-[18px] sm:text-[22px] lg:text-[28px] font-bebas font-normal leading-[1.2] text-left text-[#3f1651]">
              Clear, Predictable Pricing
            </h3>
            
            <p className="text-[13px] sm:text-[15px] lg:text-base font-poppins font-normal leading-[1.6] text-left text-[#666666]">
              One subscription. Three plans. Unlimited requests. No surprises.
            </p>
          </div>

          {/* Execution Aligned */}
          <div className="flex flex-col gap-4 sm:gap-5 justify-start items-start w-full h-auto border-2 border-[#8923b3] rounded-[24px] bg-white p-5 sm:p-6 lg:p-9">
            <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-[#8923b3] rounded-[16px] p-3 sm:p-3.5 lg:p-4">
              <Image src="/images/img_icon_white_a700_64x64.svg" alt="Execution icon" width={64} height={64} className="w-full h-full" />
            </div>
            
            <h3 className="text-[18px] sm:text-[22px] lg:text-[28px] font-bebas font-normal leading-[1.2] text-left text-[#3f1651]">
              Execution Aligned With Your Goals
            </h3>
            
            <p className="text-[13px] sm:text-[15px] lg:text-base font-poppins font-normal leading-[1.6] text-left text-[#666666]">
              We create with outcomes in mind — not just design for design sake.
            </p>
          </div>

          {/* Philosophy */}
          <div className="flex flex-col gap-4 sm:gap-5 justify-start items-start w-full h-auto border-2 border-[#8923b3] rounded-[24px] bg-white p-5 sm:p-6 lg:p-9">
            <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-[#8923b3] rounded-[16px] p-3 sm:p-3.5 lg:p-4">
              <Image src="/images/img_icon_64x64.svg" alt="Philosophy icon" width={64} height={64} className="w-full h-full" />
            </div>
            
            <h3 className="text-[18px] sm:text-[22px] lg:text-[28px] font-bebas font-normal leading-[1.2] text-left text-[#3f1651]">
              A Philosophy Rooted in Standing Out
            </h3>
            
            <p className="text-[13px] sm:text-[15px] lg:text-base font-poppins font-normal leading-[1.6] text-left text-[#666666]">
              We help your brand look remarkable so your company becomes the one people talk about.
            </p>
          </div>
        </div>

        {/* CTA Button */}
        <div className="w-full relative z-10">
          <Button
            text="WORK WITH A TEAM OBSESSED WITH REMARKABLE"
            text_font_size="text-xs sm:text-sm"
            text_font_family="Helvetica"
            text_font_weight="font-medium"
            text_line_height="leading-normal"
            text_text_align="center"
            text_text_transform="uppercase"
            text_color="text-white"
            fill_background_color="bg-[#8923b3]"
            border_border_radius="rounded-full"
            className="w-full py-4 sm:py-5 px-8 hover:bg-[#7a1f9e] transition-all duration-300 tracking-wider"
            onClick={handleCTA}
          />
        </div>
      </div>
    </section>
  )
}
